/**
 * PointSet — the typed, geocoded dataset M4 renders. It's the join of M3's
 * typed columns (name + type + sample values) with M2's geocoded coordinates
 * (lat/lng + per-row confidence), plus bounds and count. M4 owns this adapter
 * because neither M3 nor M2 emits it directly: M3 gives profiled rows, M2 gives
 * coordinates keyed by row_key; `buildPointSet` stitches them.
 *
 * `toGeoJSON` produces the single FeatureCollection the renderer installs as one
 * MapLibre source — one feature per row, `_id` preserved so NL highlight
 * overlays can target specific rows without mutating the saved spec.
 */

import type { ColumnProfile, InferredType, ParsedRow } from "@/lib/ingest";
import { boundsOf, type Bounds } from "@/lib/geo/geoMath";

export type PointColumnType = InferredType;

export interface PointColumn {
  name: string;
  type: PointColumnType;
  /** A few distinct sample values (drives the style panel + M5 routing). */
  distinctSample: string[];
}

export interface ResolvedCoord {
  lat: number;
  lng: number;
  /** 0..1 geocode confidence from M2 (1 for coordinate-mode passthrough). */
  confidence: number;
}

export interface Point {
  id: string;
  lat: number;
  lng: number;
  confidence: number;
  props: Record<string, string>;
}

// The bbox loop + the `Bounds` shape live ONCE in the geo foundation
// (`@/lib/geo/geoMath`); re-exported here so `@/lib/map/pointset` and the
// `@/lib/map` barrel keep exporting them unchanged.
export type { Bounds };
export { boundsOf };

export interface PointSet {
  columns: PointColumn[];
  points: Point[];
  bounds: Bounds | null;
  count: number;
}

export function columnsFromProfiles(profiles: ColumnProfile[]): PointColumn[] {
  return profiles.map((p) => ({ name: p.header, type: p.inferred_type, distinctSample: p.sample_values }));
}

/**
 * Build a PointSet from profiled rows and a per-row coordinate resolver. Only
 * `ready` rows that resolve to a coordinate become points; unresolved rows are
 * omitted (they live in M3's attention tray, not on the map). `props` maps each
 * column header to that row's cell value.
 */
export function buildPointSet(
  profiles: ColumnProfile[],
  rows: ParsedRow[],
  coordFor: (row: ParsedRow) => ResolvedCoord | null,
): PointSet {
  const columns = columnsFromProfiles(profiles);
  const points: Point[] = [];
  for (const row of rows) {
    if (row.status !== "ready" || row.row_key === "") continue;
    const coord = coordFor(row);
    if (!coord) continue;
    const props: Record<string, string> = {};
    profiles.forEach((p) => {
      props[p.header] = row.cells[p.index] ?? "";
    });
    points.push({ id: row.row_key, lat: coord.lat, lng: coord.lng, confidence: coord.confidence, props });
  }
  return { columns, points, bounds: boundsOf(points), count: points.length };
}

/** GeoJSON FeatureCollection shape (structurally compatible with MapLibre). */
export interface PointFeature {
  type: "Feature";
  id: number;
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: Record<string, string | number>;
}
export interface PointFeatureCollection {
  type: "FeatureCollection";
  features: PointFeature[];
}

/**
 * PointSet → GeoJSON. Each row's props become feature properties; `_id`
 * (row id) and `_confidence` are added under reserved keys for highlight
 * overlays and low-confidence styling. Numeric feature `id` is the array index.
 */
export function toGeoJSON(pointSet: PointSet): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: pointSet.points.map((p, i) => ({
      type: "Feature",
      id: i,
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { ...p.props, _id: p.id, _confidence: p.confidence },
    })),
  };
}
